import PropTypes from 'prop-types';
import {
	Box,
	Typography,
	LinearProgress,
	Skeleton as MuiSkeleton,
} from '@mui/material';
import StatusPill from 'features/IRWorkstation/components/Common/StatusPill';

const CARD_HEIGHT = '230px';
const HEADER_HEIGHT = '65px';
const STATS_HEIGHT = '40px';
const PROGRESS_HEIGHT = '20px';
const FOOTER_HEIGHT = '32px';

const EntityCard = ({ card, onClick }) => (
	<Box
		onClick={onClick}
		sx={{
			backgroundColor: 'background.paper',
			border: 'solid',
			borderColor: card.activeRaise ? 'primary.main' : 'border.light',
			borderWidth: card.activeRaise ? '2px' : '0.5px',
			borderRadius: '12px',
			padding: '12px',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: '8px',
			cursor: 'pointer',
			height: CARD_HEIGHT,
			'&:hover': { borderColor: 'primary.main' },
		}}
	>
		<Box
			sx={{
				height: HEADER_HEIGHT,
				overflow: 'hidden',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
				width: '100%',
			}}
		>
			<Typography noWrap sx={{ fontSize: '13px', fontWeight: 600 }}>
				{card.name}
			</Typography>
			<Typography color='text.secondary' sx={{ fontSize: '11px' }}>
				{card.entityType}
			</Typography>
			{card.status && (
				<StatusPill label={card.status} color='blue' sx={{ mt: '4px' }} />
			)}
		</Box>
		<Box sx={{ width: '100%', height: STATS_HEIGHT, overflow: 'hidden' }}>
			{card.stats.map((s) => (
				<Box
					key={s.label}
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'baseline',
						py: '3px',
					}}
				>
					<Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
						{s.label}
					</Typography>
					<Typography
						sx={{
							fontSize: '12px',
							fontWeight: 600,
							color:
								s.valueColor === 'primary'
									? 'primary.main'
									: s.valueColor === 'success'
										? 'signal.green'
										: s.valueColor
											? `status.${s.valueColor}.text`
											: 'text.primary',
						}}
					>
						{s.value}
					</Typography>
				</Box>
			))}
		</Box>
		<Box
			sx={{
				height: PROGRESS_HEIGHT,
				display: 'flex',
				alignItems: 'center',
				width: '100%',
			}}
		>
			{card.bar ? (
				<LinearProgress
					variant='determinate'
					value={card.bar.percent}
					sx={{
						height: 6,
						width: '100%',
						borderRadius: 2,
						backgroundColor: 'background.subtle',
						'& .MuiLinearProgress-bar': {
							borderRadius: 2,
							backgroundColor:
								card.bar.color === 'success' ? 'signal.green' : 'primary.main',
						},
					}}
				/>
			) : (
				<Typography
					sx={{
						fontSize: '11px',
						color: 'text.secondary',
					}}
				>
					No Target Size set
				</Typography>
			)}
		</Box>
		<Box sx={{ flex: 1 }} />
		<Box
			sx={{
				width: 'calc(100% + 24px)',
				mx: '-12px',
				mb: '-12px',
				px: '12px',
				py: '8px',
				height: FOOTER_HEIGHT,
				overflow: 'hidden',
				borderTop: '0.5px solid',
				borderColor: 'border.light',
				borderRadius: '0 0 12px 12px',
				backgroundColor: 'background.subtle',
			}}
		>
			<Typography
				noWrap
				sx={{
					fontSize: '11px',
					color: card.footerHighlight ? 'primary.main' : 'text.secondary',
					fontWeight: card.footerHighlight ? 600 : 400,
				}}
			>
				{card.footer}
			</Typography>
		</Box>
	</Box>
);

EntityCard.propTypes = {
	card: PropTypes.object.isRequired,
	onClick: PropTypes.func,
};

const Skeleton = () => (
	<Box
		sx={{
			backgroundColor: 'background.paper',
			border: '0.5px solid',
			borderColor: 'border.light',
			borderRadius: '12px',
			padding: '12px',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: '8px',
			height: CARD_HEIGHT,
		}}
	>
		<Box
			sx={{
				width: '100%',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
				pb: '8px',
			}}
		>
			<MuiSkeleton width='60%' height={16} />
			<MuiSkeleton width='40%' height={12} sx={{ mt: 1 }} />
		</Box>
		<Box sx={{ width: '100%' }}>
			<MuiSkeleton width='100%' height={12} sx={{ mb: 1 }} />
			<MuiSkeleton width='100%' height={12} />
		</Box>
		<MuiSkeleton
			variant='rectangular'
			width='100%'
			height={6}
			sx={{ borderRadius: 2 }}
		/>
		<Box sx={{ flex: 1 }} />
		<Box
			sx={{
				width: 'calc(100% + 24px)',
				mx: '-12px',
				mb: '-12px',
				px: '12px',
				py: '8px',
				borderTop: '0.5px solid',
				borderColor: 'border.light',
				borderRadius: '0 0 12px 12px',
				backgroundColor: 'background.subtle',
			}}
		>
			<MuiSkeleton width='80%' height={12} />
		</Box>
	</Box>
);

Skeleton.displayName = 'EntityCard.Skeleton';
EntityCard.Skeleton = Skeleton;

const EntityCardError = ({ message }) => (
	<Box
		sx={{
			backgroundColor: 'background.paper',
			border: '0.5px solid',
			borderColor: 'border.light',
			borderRadius: '12px',
			padding: '12px',
			height: CARD_HEIGHT,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		}}
	>
		<Typography
			sx={{ fontSize: '12px', color: 'error.main', textAlign: 'center' }}
		>
			{message || 'Failed to load entity'}
		</Typography>
	</Box>
);

EntityCardError.displayName = 'EntityCard.Error';
EntityCardError.propTypes = {
	message: PropTypes.string,
};
EntityCard.Error = EntityCardError;

const EntityCardEmpty = () => (
	<Box
		sx={{
			backgroundColor: 'background.paper',
			border: '0.5px solid',
			borderColor: 'border.light',
			borderRadius: '12px',
			padding: '12px',
			height: CARD_HEIGHT,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		}}
	>
		<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
			No entities found
		</Typography>
	</Box>
);

EntityCardEmpty.displayName = 'EntityCard.Empty';
EntityCard.Empty = EntityCardEmpty;

export default EntityCard;
